import { useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { type ServiceData } from "@/mocks/services";
import { services, PHONE } from "@/mocks/updiag";
import {
  schemaLocalBusiness,
  schemaBreadcrumb,
  schemaService,
  schemaFaqPage,
  renderSchema,
} from "@/lib/schemas";
import { servicesDeepDive } from "@/mocks/servicesDeepDive";
import { SHOW_REVIEWS } from "@/config/features";

interface Props {
  data: ServiceData;
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${open ? "border-[#F97316]" : "border-gray-100"}`}>
      <button
        className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <span className={`font-semibold text-sm pr-4 ${open ? "text-[#F97316]" : "text-[#0F3B5F]"}`}>{q}</span>
        <i className={`ri-arrow-down-s-line text-xl flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#F97316]" : "text-gray-400"}`}></i>
      </button>
      {open && (
        <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-4">{a}</div>
      )}
    </div>
  );
}

export default function ServicePage({ data }: Props) {
  const relatedServices = services.filter((s) => data.related.includes(s.slug));

  return (
    <>
      <title>{data.metaTitle}</title>
      <meta name="description" content={data.metaDesc} />
      <link rel="canonical" href={`https://updiag.fr/diagnostics/${data.slug}`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(schemaLocalBusiness())}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(
          schemaBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Nos diagnostics", url: "/diagnostics" },
            { name: data.fullName, url: `/diagnostics/${data.slug}` },
          ])
        )}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(
          schemaService(data.fullName, data.whatIs, data.price)
        )}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(schemaFaqPage(data.faq))}
      />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img src={data.heroImage} alt={data.fullName} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-[#0F3B5F]/82"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <Link to="/diagnostics" className="text-white/60 text-sm hover:text-white transition-colors">
                <i className="ri-arrow-left-line mr-1"></i>Tous les diagnostics
              </Link>
              {data.badge && (
                <span className="bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full">{data.badge}</span>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="w-16 h-16 flex items-center justify-center bg-white/15 rounded-2xl mb-5">
                  <i className={`${data.icon} text-white text-3xl`}></i>
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight" style={{ fontFamily: "Barlow, sans-serif" }}>
                  {data.heroTitle}
                </h1>
                <p className="text-lg text-white/80 mb-8">{data.heroSubtitle}</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/devis" className="flex items-center justify-center gap-2 px-7 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                    <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
                  </Link>
                  <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-7 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap">
                    <i className="ri-phone-fill"></i> {PHONE}
                  </a>
                </div>
              </div>
              {/* Price card */}
              <div className="flex justify-center lg:justify-end">
                <div className="bg-white rounded-2xl p-8 w-full max-w-sm">
                  <div className="text-center mb-6">
                    <div className="text-4xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>{data.price}</div>
                    <div className="text-sm text-gray-400">{data.priceDetail}</div>
                  </div>
                  <div className="flex flex-col gap-3 mb-6">
                    {[
                      { icon: "ri-checkbox-circle-fill", text: "Certifié COFRAC", color: "text-[#16A34A]" },
                      { icon: "ri-timer-flash-fill", text: "RDV sous 48h", color: "text-[#F97316]" },
                      { icon: "ri-file-check-line", text: "Rapport le jour même", color: "text-[#0F3B5F]" },
                      { icon: "ri-shield-check-fill", text: "RC Pro assurée", color: "text-[#16A34A]" },
                    ].map((item) => (
                      <div key={item.text} className="flex items-center gap-3 text-sm text-gray-600">
                        <i className={`${item.icon} ${item.color} text-lg`}></i>
                        {item.text}
                      </div>
                    ))}
                  </div>
                  <Link to="/devis" className="block w-full text-center py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                    Devis gratuit en 2 min
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Qu'est-ce que */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-3">
                <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                  Définition
                </span>
                <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Qu'est-ce que le {data.name} ?
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">{data.whatIs}</p>
                <p className="text-gray-600 leading-relaxed">{data.whatIsDetail}</p>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                      <i className="ri-alert-fill text-amber-600 text-xl"></i>
                    </div>
                    <h3 className="font-bold text-amber-800" style={{ fontFamily: "Barlow, sans-serif" }}>Obligations légales</h3>
                  </div>
                  <p className="text-sm text-amber-700 mb-4">{data.obligations}</p>
                  <ul className="flex flex-col gap-2">
                    {data.obligationItems.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-amber-800">
                        <i className="ri-checkbox-circle-fill text-amber-600 mt-0.5 flex-shrink-0"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Déroulement */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-orange-100 text-[#F97316] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Notre processus
              </span>
              <h2 className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                Déroulement de l'intervention
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              {data.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-5 bg-white rounded-2xl p-6 border border-gray-100 hover:border-orange-200 transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F97316]/10 rounded-xl flex-shrink-0">
                    <i className={`${step.icon} text-[#F97316] text-xl`}></i>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-extrabold text-gray-100 leading-none mt-1" style={{ fontFamily: "Barlow, sans-serif" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-bold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>{step.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Checklist + Erreurs */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Checklist */}
              <div className="bg-green-50 rounded-2xl p-7 border border-green-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] text-xl"></i>
                  </div>
                  <h3 className="font-bold text-[#0F3B5F] text-lg" style={{ fontFamily: "Barlow, sans-serif" }}>
                    Ce qu'il faut préparer
                  </h3>
                </div>
                <ul className="flex flex-col gap-3">
                  {data.checklist.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Erreurs */}
              <div className="bg-red-50 rounded-2xl p-7 border border-red-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl">
                    <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-[#0F3B5F] text-lg" style={{ fontFamily: "Barlow, sans-serif" }}>
                    Erreurs à éviter
                  </h3>
                </div>
                <ul className="flex flex-col gap-3">
                  {data.errors.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                      <i className="ri-close-circle-fill text-red-400 mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifs */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-orange-100 text-[#F97316] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Tarifs
              </span>
              <h2 className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                Nos prix pour le {data.name}
              </h2>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0F3B5F] text-white">
                    <th className="text-left px-6 py-4 text-sm font-bold" style={{ fontFamily: "Barlow, sans-serif" }}>Type de bien</th>
                    <th className="text-right px-6 py-4 text-sm font-bold">Prix TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {data.prices.map((row, i) => (
                    <tr key={i} className={`border-b border-gray-50 hover:bg-orange-50/40 transition-colors ${row.highlight ? "border-l-4 border-l-[#F97316]" : ""} ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                      <td className="px-6 py-4 text-sm font-medium text-[#0F3B5F]">{row.label}</td>
                      <td className="px-6 py-4 text-right text-lg font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>{row.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center">
              <Link to="/devis" className="inline-flex items-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                <i className="ri-file-list-3-line"></i> Obtenir mon devis personnalisé
              </Link>
            </div>
          </div>
        </section>

        {/* Deep dive — prose SEO long-form */}
        {servicesDeepDive[data.slug] && (
          <section className="bg-gray-50 py-20">
            <div className="max-w-3xl mx-auto px-6">
              <div className="text-center mb-10">
                <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                  Informations détaillées
                </span>
                <h2
                  className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F]"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  Tout savoir sur le {data.name}
                </h2>
              </div>

              <article className="space-y-6">
                {servicesDeepDive[data.slug].map((section, i) => (
                  <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                    <h3
                      className="text-xl md:text-2xl font-extrabold text-[#0F3B5F] mb-4"
                      style={{ fontFamily: "Barlow, sans-serif" }}
                    >
                      {section.h2}
                    </h3>
                    <div
                      className="text-gray-700 leading-relaxed [&_p]:mb-3 [&_strong]:text-[#0F3B5F] [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-3 [&_li]:mb-1.5"
                      dangerouslySetInnerHTML={{ __html: section.html }}
                    />
                  </div>
                ))}
              </article>
            </div>
          </section>
        )}

        {/* ═════════ Chiffres clés du service ═════════ */}
        <section className="relative py-20 overflow-hidden bg-gradient-to-br from-[#0A2B47] via-[#0F3B5F] to-[#1A4A76]">
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          ></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 bg-[#F97316]/15 border border-[#F97316]/30 text-[#FDBA74] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                <i className="ri-bar-chart-2-line"></i>
                Chiffres clés
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-white mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Up Diag en chiffres
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                Une expertise reconnue par des milliers de clients à Paris et en Île-de-France.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-[#F97316]/20 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-[#FDBA74] text-2xl"></i>
                </div>
                <div
                  className="text-3xl md:text-4xl font-extrabold text-white mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  +2 500
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Diagnostics réalisés</div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-green-400/20 flex items-center justify-center">
                  <i className="ri-time-line text-green-300 text-2xl animate-pulse"></i>
                </div>
                <div
                  className="text-3xl md:text-4xl font-extrabold text-white mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  48<span className="text-green-300">h</span>
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Délai d'intervention</div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                  <i className={`${SHOW_REVIEWS ? "ri-star-fill" : "ri-file-shield-2-line"} text-[#FDBA74] text-2xl`}></i>
                </div>
                <div
                  className="text-3xl md:text-4xl font-extrabold text-white mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  {SHOW_REVIEWS ? <>4.9<span className="text-[#FDBA74]">/5</span></> : <>100<span className="text-[#FDBA74]">%</span></>}
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wider">
                  {SHOW_REVIEWS ? "127 avis Google" : "Rapports opposables"}
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-[#F97316]/20 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-[#FDBA74] text-2xl"></i>
                </div>
                <div
                  className="text-3xl md:text-4xl font-extrabold text-white mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  10
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Ans d'expertise</div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <div className="w-12 h-12 rounded-xl bg-[#16A34A]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-award-line text-green-300 text-xl"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Certifié Cofrac</div>
                  <div className="text-xs text-white/60">Accréditation officielle</div>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <div className="w-12 h-12 rounded-xl bg-[#F97316]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-shield-2-line text-[#FDBA74] text-xl"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Rapport opposable</div>
                  <div className="text-xs text-white/60">Valeur juridique pleine</div>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-umbrella-line text-[#FDBA74] text-xl"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Assurance RC Pro</div>
                  <div className="text-xs text-white/60">Couverture complète</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">FAQ</span>
              <h2 className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                Questions fréquentes sur le {data.name}
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              {data.faq.map((item, i) => (
                <FaqItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Services connexes */}
        {relatedServices.length > 0 && (
          <section className="bg-[#F8FAFC] py-20">
            <div className="max-w-5xl mx-auto px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Services connexes
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedServices.map((svc) => (
                  <Link key={svc.id} to={`/diagnostics/${svc.slug}`} className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 block">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0F3B5F]/8 rounded-xl mb-4 group-hover:bg-[#F97316]/10 transition-colors">
                      <i className={`${svc.icon} text-[#0F3B5F] group-hover:text-[#F97316] text-2xl transition-colors`}></i>
                    </div>
                    <h3 className="font-bold text-[#0F3B5F] mb-1 group-hover:text-[#F97316] transition-colors" style={{ fontFamily: "Barlow, sans-serif" }}>{svc.name}</h3>
                    <p className="text-xs text-gray-400 mb-3">{svc.fullName}</p>
                    <span className="text-lg font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>{svc.price}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ═════════ Zone d'intervention ═════════ */}
        <section className="bg-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Intervention locale
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                {data.name} dans toute l'Île-de-France
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Nos équipes interviennent à Paris et dans les 7 départements franciliens — délai garanti
                sous 48h, rapport certifié le jour même.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { code: "75", nom: "Paris", slug: "paris-75", featured: true },
                { code: "92", nom: "Hauts-de-Seine", slug: "hauts-de-seine-92" },
                { code: "93", nom: "Seine-Saint-Denis", slug: "seine-saint-denis-93" },
                { code: "94", nom: "Val-de-Marne", slug: "val-de-marne-94" },
                { code: "78", nom: "Yvelines", slug: "yvelines-78" },
                { code: "91", nom: "Essonne", slug: "essonne-91" },
                { code: "77", nom: "Seine-et-Marne", slug: "seine-et-marne-77" },
                { code: "95", nom: "Val-d'Oise", slug: "val-d-oise-95" },
              ].map((d) => (
                <Link
                  key={d.code}
                  to={`/zones/${d.slug}`}
                  className={`group relative overflow-hidden rounded-2xl p-5 border-2 transition-all duration-300 hover:-translate-y-1 ${
                    d.featured
                      ? "bg-gradient-to-br from-[#0F3B5F] to-[#1A4A76] border-[#0F3B5F] text-white hover:shadow-xl"
                      : "bg-white border-gray-100 hover:border-[#F97316] hover:shadow-lg"
                  }`}
                >
                  <div
                    className={`absolute -top-4 -right-4 text-6xl font-extrabold opacity-10 ${
                      d.featured ? "text-white" : "text-[#0F3B5F]"
                    }`}
                    style={{ fontFamily: "Barlow, sans-serif" }}
                  >
                    {d.code}
                  </div>
                  <div className="relative">
                    <div
                      className={`text-xs uppercase tracking-wider mb-2 font-semibold ${
                        d.featured ? "text-[#FDBA74]" : "text-[#F97316]"
                      }`}
                    >
                      Département {d.code}
                    </div>
                    <div
                      className={`text-lg font-bold mb-3 ${
                        d.featured ? "text-white" : "text-[#0F3B5F]"
                      }`}
                      style={{ fontFamily: "Barlow, sans-serif" }}
                    >
                      {d.nom}
                    </div>
                    <div
                      className={`flex items-center gap-1 text-sm font-semibold ${
                        d.featured ? "text-[#FDBA74]" : "text-[#F97316]"
                      }`}
                    >
                      {data.name} {d.nom.replace("Paris", "")}
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Besoin d'un {data.name} à Paris ou en IDF ?
            </h2>
            <p className="text-white/75 mb-8">Devis gratuit en 2 minutes — Intervention sous 48h — Rapport le jour même</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis" className="flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
              </Link>
              <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-phone-fill"></i> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
